/**
 * PATH: packages/email-hub/src/feature/reader-header/workflow/useWorkflowCells.ts
 * FILE_ID: EMAILHUB-READERHEADER-WORKFLOW-CELLS-001
 * PURPOSE: Runner-backed view models for the three workflow cells. Replaces
 *   the rail's ONE shared busyKey with the registry's three independent busy
 *   keys (workflow:owner / workflow:due / workflow:watchers) so an owner
 *   change never blocks a due change (change #269 P5 exit gate).
 * ROLE IN ARCHITECTURE: Behavior hook between useEmailActions transports and
 *   the reused email-allocation controls.
 * MAIN EXPORTS: useWorkflowCells, WorkflowCellsViewModel.
 * NON-RESPONSIBILITIES: Rendering (WorkflowRow), menu building (menus/),
 *   derivations (workflowModel), transports (useEmails owns the API).
 * NOTES FOR FUTURE AI: The runner here has NO reconcile — every mutation
 *   patches the email from the RETURNED payload (mutation economy, plan
 *   "Optimism, undo, failure, and concurrency"); failure rolls back only the
 *   affected cell's field. Settled mutations publish a multi-tab sync event.
 */
import { useCallback, useMemo, useState } from 'react'

import { useToast } from '../../../_internal/components/Toast'
import type { AllocationMember } from '../../components/email-allocation'
import { formatScheduledFor } from '../../components/email-allocation'
import {
  useEmailActions,
  type Email,
  type EmailAssignment,
  type EmailWatcher,
} from '../../hooks/useEmails'
import { getReaderAction } from '../actions/registry'
import type { ReaderHeaderSurface } from '../model/types'
import { useReaderActionRunner } from '../runtime/useReaderActionRunner'
import { publishReaderMutation } from '../runtime/multiTabSync'
import type {
  AllocationBucket,
  AssignmentViewModel,
  DueBucketViewModel,
  WatchersViewModel,
} from '../../components/email-allocation'
import {
  buildWatcherListText,
  deriveAssigneeMember,
  deriveInviteCandidates,
  deriveOwnerStatus,
  predictAssignment,
  predictWatcher,
  splitWatchersForDisplay,
  type OwnerStatus,
} from './workflowModel'

export interface WorkflowCellsInput {
  email: Email
  members: AllocationMember[]
  onPatchEmail?: (emailId: string, patch: Partial<Email>) => void
  surface?: ReaderHeaderSurface
  /** Host ping popover opener — powers "Ping owner"/"Ping watcher". */
  onPing?: () => void
  /** Host profile navigation — powers "Open profile". Absent → item omitted. */
  onOpenProfile?: (userId: number) => void
}

export interface WorkflowOwnerViewModel extends AssignmentViewModel {
  status: OwnerStatus
}

export interface WorkflowDueViewModel extends DueBucketViewModel {
  pickDate: (isoDate: string) => Promise<void>
  clearDue: () => Promise<void>
  copyDate: () => void
  pickerOpen: boolean
  setPickerOpen: (open: boolean) => void
}

export interface WorkflowWatchersViewModel extends WatchersViewModel {
  visible: EmailWatcher[]
  overflowCount: number
  copyList: () => void
}

export interface WorkflowCellsViewModel {
  owner: WorkflowOwnerViewModel
  due: WorkflowDueViewModel
  watchers: WorkflowWatchersViewModel
  onPing?: () => void
  onOpenProfile?: (userId: number) => void
}

export function useWorkflowCells({
  email,
  members,
  onPatchEmail,
  surface = 'preview',
  onPing,
  onOpenProfile,
}: WorkflowCellsInput): WorkflowCellsViewModel {
  const { toast } = useToast()
  const actions = useEmailActions()
  // Reconcile-free runner: workflow mutations patch from returned payloads.
  const runner = useReaderActionRunner({ surface })

  const [ownerPickerOpen, setOwnerPickerOpen] = useState(false)
  const [duePickerOpen, setDuePickerOpen] = useState(false)
  const [watcherManagerOpen, setWatcherManagerOpen] = useState(false)

  const assignment = email.assignment ?? null
  const watcherList = useMemo(() => email.watchers ?? [], [email.watchers])
  const assigneeUserId = assignment?.assignee_user_id ?? null

  // phaseFor is state-backed, so busy transitions re-render the cells.
  const ownerBusy = runner.phaseFor('workflow:owner') !== 'idle'
  const dueBusy = runner.phaseFor('workflow:due') !== 'idle'
  const watchersBusy = runner.phaseFor('workflow:watchers') !== 'idle'

  const patch = useCallback(
    (fields: Partial<Email>) => onPatchEmail?.(email.id, fields),
    [onPatchEmail, email.id],
  )
  const publish = useCallback(
    () => publishReaderMutation(email.id, 'workflow'),
    [email.id],
  )

  /** Inverse transport for the 8s undo toast: restore the prior assignment. */
  const restoreAssignment = useCallback(
    async (prior: EmailAssignment | null) => {
      if (prior === null) {
        await actions.clearEmailAssignment(email.id)
        patch({ assignment: null })
        return
      }
      const restored = await actions.setEmailAssignment(email.id, {
        assignee_user_id: prior.assignee_user_id,
        bucket: prior.bucket,
        scheduled_for: prior.scheduled_for,
      })
      patch({ assignment: restored })
    },
    [actions, email.id, patch],
  )

  const mutateAssignment = useCallback(
    (
      actionId: 'manage_owner' | 'manage_due',
      predicted: EmailAssignment | null,
      transport: () => Promise<EmailAssignment | null>,
      successToast: string,
    ) => {
      const prior = assignment
      return runner.run({
        action: getReaderAction(actionId),
        optimistic: onPatchEmail
          ? {
              apply: () => patch({ assignment: predicted }),
              rollback: () => patch({ assignment: prior }),
            }
          : undefined,
        execute: async () => {
          const next = await transport()
          patch({ assignment: next })
          publish()
        },
        undo: {
          execute: () => restoreAssignment(prior),
          onRestored: publish,
        },
        successToast,
      })
    },
    [assignment, runner, onPatchEmail, patch, publish, restoreAssignment],
  )

  const pickAssignee = useCallback(
    async (member: AllocationMember | null) => {
      setOwnerPickerOpen(false)
      if (member === null && assignment === null) return
      await mutateAssignment(
        'manage_owner',
        predictAssignment(assignment, member),
        async () => {
          if (member === null) {
            await actions.clearEmailAssignment(email.id)
            return null
          }
          return actions.setEmailAssignment(email.id, {
            assignee_user_id: member.id,
          })
        },
        member ? `Assigned to ${member.name}` : 'Assignee cleared',
      )
    },
    [assignment, mutateAssignment, actions, email.id],
  )

  const pickBucket = useCallback(
    async (bucket: AllocationBucket) => {
      setDuePickerOpen(false)
      await mutateAssignment(
        'manage_due',
        predictAssignment(assignment, null, {
          bucket,
          scheduled_for:
            bucket === 'no_date' ? null : assignment?.scheduled_for ?? null,
        }),
        () => actions.setEmailAssignment(email.id, { bucket }),
        `Due set to ${bucket === 'no_date' ? 'no date' : bucket.replace('_', ' ')}`,
      )
    },
    [assignment, mutateAssignment, actions, email.id],
  )

  const pickDate = useCallback(
    async (isoDate: string) => {
      setDuePickerOpen(false)
      // Date-only ISO passthrough — tenant timezone respected because no
      // Date()/UTC conversion ever touches the value (plan Due contract).
      await mutateAssignment(
        'manage_due',
        predictAssignment(assignment, null, { scheduled_for: isoDate }),
        () => actions.setEmailAssignment(email.id, { scheduled_for: isoDate }),
        `Due ${formatScheduledFor(isoDate) ?? isoDate}`,
      )
    },
    [assignment, mutateAssignment, actions, email.id],
  )

  const clearDue = useCallback(() => pickBucket('no_date'), [pickBucket])

  const copyDate = useCallback(() => {
    runner.runLocal(getReaderAction('manage_due'), () => {
      const value = assignment?.scheduled_for
      if (!value) {
        toast('No due date to copy', 'info')
        return
      }
      void navigator.clipboard?.writeText(value)
      toast('Due date copied', 'success')
    })
  }, [runner, assignment, toast])

  const inviteWatcher = useCallback(
    async (member: AllocationMember) => {
      setWatcherManagerOpen(false)
      const prior = watcherList
      const without = prior.filter((w) => w.user_id !== member.id)
      await runner.run({
        action: getReaderAction('manage_watchers'),
        optimistic: onPatchEmail
          ? {
              apply: () =>
                patch({ watchers: [...without, predictWatcher(member)] }),
              rollback: () => patch({ watchers: prior }),
            }
          : undefined,
        execute: async () => {
          const created = await actions.inviteEmailWatcher(email.id, member.id)
          patch({ watchers: [...without, created] })
          publish()
        },
        undo: {
          execute: async () => {
            await actions.removeEmailWatcher(email.id, member.id)
            patch({ watchers: prior })
          },
          onRestored: publish,
        },
        successToast: `Invited ${member.name}`,
      })
    },
    [watcherList, runner, onPatchEmail, patch, actions, email.id, publish],
  )

  const removeWatcher = useCallback(
    async (watcher: EmailWatcher) => {
      const prior = watcherList
      const without = prior.filter((w) => w.user_id !== watcher.user_id)
      await runner.run({
        action: getReaderAction('manage_watchers'),
        optimistic: onPatchEmail
          ? {
              apply: () => patch({ watchers: without }),
              rollback: () => patch({ watchers: prior }),
            }
          : undefined,
        execute: async () => {
          await actions.removeEmailWatcher(email.id, watcher.user_id)
          patch({ watchers: without })
          publish()
        },
        undo: {
          execute: async () => {
            const revived = await actions.inviteEmailWatcher(
              email.id,
              watcher.user_id,
            )
            patch({ watchers: [...without, revived] })
          },
          onRestored: publish,
        },
        successToast: `Removed ${watcher.user_name || 'watcher'}`,
      })
    },
    [watcherList, runner, onPatchEmail, patch, actions, email.id, publish],
  )

  const copyList = useCallback(() => {
    runner.runLocal(getReaderAction('manage_watchers'), () => {
      if (watcherList.length === 0) {
        toast('No watchers to copy', 'info')
        return
      }
      void navigator.clipboard?.writeText(buildWatcherListText(watcherList))
      toast('Watcher list copied', 'success')
    })
  }, [runner, watcherList, toast])

  const { visible, overflowCount } = splitWatchersForDisplay(watcherList)

  return {
    owner: {
      assignment,
      members,
      busyKey: ownerBusy ? 'assignee:busy' : null,
      showPicker: ownerPickerOpen,
      setShowPicker: setOwnerPickerOpen,
      assigneeUserId,
      assigneeMember: deriveAssigneeMember(members, assignment),
      pickAssignee,
      status: deriveOwnerStatus(assignment, members),
    },
    due: {
      busyKey: dueBusy ? 'bucket:busy' : null,
      currentBucket: assignment?.bucket ?? 'no_date',
      scheduledForLabel: formatScheduledFor(assignment?.scheduled_for),
      pickBucket,
      pickDate,
      clearDue,
      copyDate,
      pickerOpen: duePickerOpen,
      setPickerOpen: setDuePickerOpen,
    },
    watchers: {
      members,
      watcherList,
      inviteCandidates: deriveInviteCandidates(
        members,
        watcherList,
        assigneeUserId,
      ),
      busyKey: watchersBusy ? 'watcher:busy' : null,
      showPicker: watcherManagerOpen,
      setShowPicker: setWatcherManagerOpen,
      inviteWatcher,
      removeWatcher,
      visible,
      overflowCount,
      copyList,
    },
    onPing,
    onOpenProfile,
  }
}
